import React, { useEffect, useState, useRef, useMemo } from 'react';
import { BrandButton, BrandOutlinedButton, CampaignStatusButton } from '../button';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  TableFooter,
  TablePagination,
  Divider,
  Tooltip,
  Tabs,
  Tab,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import BarChartIcon from '@mui/icons-material/BarChart';
import PreviewIcon from '@mui/icons-material/Preview';
import EmailIcon from '@mui/icons-material/Email';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import UnsubscribeIcon from '@mui/icons-material/Unsubscribe';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import { green, orange, red, blue, grey } from '@mui/material/colors';
import * as XLSX from 'xlsx';

// Helper function to calculate percentage
const getPercentage = (value, total) => {
  if (!total || total === 0) return '0%';
  return `${Math.round((value / total) * 100)}%`;
};

function processHTMLForPreview(html) {
  if (!html) return html;

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const images = tempDiv.querySelectorAll('img');
  images.forEach(img => {
    const src = img.getAttribute('src');
    const alt = img.getAttribute('alt') || 'Image';

    if (src && (src.startsWith('http') || src.startsWith('https') || src.startsWith('cid:'))) {
      const placeholder = document.createElement('div');
      placeholder.style.cssText = `
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background-color: #f0f0f0;
        border: 2px dashed #ccc;
        color: #666;
        font-size: 12px;
        font-weight: bold;
        text-align: center;
        min-width: 80px;
        min-height: 40px;
        padding: 8px;
        border-radius: 4px;
        margin: 4px;
      `;

      const width = img.getAttribute('width') || img.style.width;
      const height = img.getAttribute('height') || img.style.height;

      if (width) {
        placeholder.style.width = width.includes('px') ? width : `${width}px`;
      }
      if (height) {
        placeholder.style.height = height.includes('px') ? height : `${height}px`;
      }

      placeholder.textContent = alt.length > 10 ? alt.substring(0, 10) + '...' : alt;
      img.parentNode.replaceChild(placeholder, img);
    }
  });

  tempDiv.querySelectorAll('link[rel="preload"]').forEach(link => link.remove());
  tempDiv.querySelectorAll('script').forEach(script => script.remove());

  return tempDiv.innerHTML;
}

const EmailAnalyticsDialog = ({ campaign, open, onClose }) => {
  // Use campaign values from Dashboard (passed as prop)
  const [contacts, setContacts] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [textFilters, setTextFilters] = useState({
    contact: '',
    status: '',
    dateSent: '',
    dateViewed: ''
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // New state for template preview
  const [activeTab, setActiveTab] = useState(0);
  const [emailTemplate, setEmailTemplate] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const loadedTemplateCampaignId = useRef(null);

  const campaignId = campaign?._id;

  useEffect(() => {
    if (!open || !campaignId) return;
    fetchContacts();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchContacts uses campaignId
  }, [open, campaignId]);

  useEffect(() => {
    if (!open || !campaignId) return;
    if (loadedTemplateCampaignId.current === campaignId) return;

    const loadTemplate = async () => {
      setTemplateLoading(true);
      try {
        const response = await axios.get(`/api/campaigns/${campaignId}/template`);
        setEmailTemplate(response.data);
        loadedTemplateCampaignId.current = campaignId;
      } catch (error) {
        console.error('Error fetching email template:', error);
        setEmailTemplate(null);
      } finally {
        setTemplateLoading(false);
      }
    };

    loadTemplate();
  }, [open, campaignId]);

  useEffect(() => {
    if (!open) {
      loadedTemplateCampaignId.current = null;
      setEmailTemplate(null);
      setActiveTab(0);
    }
  }, [open]);

  const previewHtml = useMemo(
    () => processHTMLForPreview(emailTemplate?.message),
    [emailTemplate?.message]
  );

  const fetchContacts = async () => {
    if (!campaignId) return;
    try {
      const response = await axios.get(`/api/campaigns/${campaignId}/contacts`);
      let contactsData = [];

      if (Array.isArray(response.data)) {
        contactsData = response.data;
      } else if (response.data.contacts && Array.isArray(response.data.contacts)) {
        contactsData = response.data.contacts;
      } else {
        // ???? ?????? ?????? ???????????, ????? ??????? ?????? ? ??????? ??? ???????
        console.warn('Unexpected contacts response format:', response.data);
      }
      setContacts(contactsData);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
    }
  };

  // Status icon based on contact status
  const getStatusIcon = (status) => {
    switch (status ? status.toLowerCase() : '') {
      case 'pending':
        return (
          <Tooltip title="Email is pending in queue">
            <PendingIcon sx={{ color: grey[500], marginRight: '5px' }} />
          </Tooltip>
        );
      case 'sending':
        return (
          <Tooltip title="Email is being sent">
            <HourglassBottomIcon sx={{ color: orange[500], marginRight: '5px' }} />
          </Tooltip>
        );
      case 'sent':
        return (
          <Tooltip title="Email was accepted by recipient server">
            <EmailIcon sx={{ color: blue[500], marginRight: '5px' }} />
          </Tooltip>
        );
      case 'delivered':
        return (
          <Tooltip title="Email was successfully delivered">
            <CheckCircleIcon sx={{ color: green[500], marginRight: '5px' }} />
          </Tooltip>
        );
      case 'opened':
        return (
          <Tooltip title="Email was opened by recipient">
            <MarkEmailReadIcon sx={{ color: green[600], marginRight: '5px' }} />
          </Tooltip>
        );
      case 'clicked':
        return (
          <Tooltip title="Recipient clicked a link in the email">
            <TouchAppIcon sx={{ color: green[700], marginRight: '5px' }} />
          </Tooltip>
        );
      case 'unsubscribed':
        return (
          <Tooltip title="Recipient unsubscribed from emails">
            <UnsubscribeIcon sx={{ color: grey[600], marginRight: '5px' }} />
          </Tooltip>
        );
      case 'deferred':
        return (
          <Tooltip title="Email delivery temporarily delayed">
            <AccessTimeIcon sx={{ color: orange[400], marginRight: '5px' }} />
          </Tooltip>
        );
      case 'failed':
        return (
          <Tooltip title="Email delivery failed">
            <ErrorOutlineIcon sx={{ color: red[500], marginRight: '5px' }} />
          </Tooltip>
        );
      case 'bounced':
        return (
          <Tooltip title="Email bounced - recipient address invalid">
            <CancelIcon sx={{ color: red[600], marginRight: '5px' }} />
          </Tooltip>
        );
      
      default:
        return (
          <Tooltip title={`Status: ${status || 'Unknown'}`}>
            <PendingIcon sx={{ color: grey[400], marginRight: '5px' }} />
          </Tooltip>
        );
    }
  };

  // "View" buttons in summary set a filter on contact status.
  const handleFilterClick = (type) => {
    setFilterType(type);
    setPage(0);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Get unique statuses from contacts for dynamic filtering
  const uniqueStatuses = [...new Set(contacts.map(contact => contact.status).filter(Boolean))].sort();

  // Filter contacts by status filter and text filters from table header.
  const filteredContacts = contacts
  .filter((contact) => {
    if (filterType === 'all') return true;
    return (contact.status || '').toLowerCase() === filterType;
  })
  .filter((contact) => {
    // Filter by contact email text using a default empty string if undefined
    const email = (contact.email || '').toLowerCase();
    const contactFilter = textFilters.contact.toLowerCase();
    if (contactFilter && !email.includes(contactFilter)) return false;
    
    // Filter by status - exact match or empty for all statuses
    if (textFilters.status && (contact.status || '') !== textFilters.status) return false;
    
    // Filter by date sent
    const dateSentStr = contact.dateSent ? new Date(contact.dateSent).toLocaleString() : '';
    const dateSentFilter = textFilters.dateSent.toLowerCase();
    if (dateSentFilter && !dateSentStr.toLowerCase().includes(dateSentFilter)) return false;
    
    // Filter by date viewed
    const dateViewedStr = contact.dateViewed ? new Date(contact.dateViewed).toLocaleString() : '';
    const dateViewedFilter = textFilters.dateViewed.toLowerCase();
    if (dateViewedFilter && !dateViewedStr.toLowerCase().includes(dateViewedFilter)) return false;
    
    return true;
  });

  const paginatedContacts = filteredContacts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const exportCampaignData = () => {
  if (!campaign) return;

  // ????????? ??????? ?????????? ????????
  const summaryData = [{
    "Campaign Name": campaign.campaignName,
    "Date Sent": campaign.sentDate,
    "Recipients": campaign.recipients || 0,
    "Opens": campaign.opens || 0,

    "Clicks": campaign.clicks || 0,
    "Unsubscribed": campaign.unsubscribed || 0,
    "Status": campaign.status || "pending"
  }];

  // ??????????? ?????? ????????? ?? state (contacts)
  const emailData = contacts.map((contact) => ({
    "Contact": contact.email,
    "Status": contact.status,
    "Date Sent": contact.dateSent,
    "Date Viewed": contact.dateViewed
  }));

  // ?????????? ??????:
  // - ??????? ??????? ??????????
  // - ?????? ?????? ??? ??????????
  // - ????????? ? ?????? ?? email
  const sheetData = [
    ...summaryData,
    {},  // ?????? ?????? ??? ??????????
    { "Contact": "Contact", "Status": "Status", "Date Sent": "Date Sent", "Date Viewed": "Date Viewed" },
    ...emailData
  ];

  // ????????? ???????? ????? ?? ??????
  const worksheet = XLSX.utils.json_to_sheet(sheetData, { skipHeader: true });

  // ???????? ????? Excel ? ?????????? ???????? ?????
  const workbook = XLSX.utils.book_new();
  // ?????????? ???????? ???????? ??? ????? ????? (???????? 31 ??????)
  const sheetName = campaign.campaignName ? campaign.campaignName.slice(0, 31) : "Campaign";
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // ?????????? ????? (????????, "Campaign.xlsx")
  XLSX.writeFile(workbook, `${sheetName}.xlsx`);
};

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      {/* Header with title and a "Send" button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
        <DialogTitle sx={{ p: 0, m: 0, fontWeight: 'bold' }}>
          Campaign Performance
        </DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          {campaign?.campaignName && (
            <Chip 
              label={campaign.campaignName} 
              variant="outlined" 
              size="small"
            />
          )}
          <CampaignStatusButton status={campaign?.status} />
        </Box>
      </Box>
      <Divider />

      {/* Tabs for switching between Statistics and Email Template */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="campaign analytics tabs">
          <Tab 
            icon={<BarChartIcon />} 
            label="Statistics" 
            sx={{ textTransform: 'none' }}
          />
          <Tab 
            icon={<PreviewIcon />} 
            label="Email Template" 
            sx={{ textTransform: 'none' }}
          />
        </Tabs>
      </Box>

      <DialogContent dividers sx={{ p: 0, height: '70vh' }}>
        {/* Statistics Tab */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            {/* Summary Section */}
            <Box display="flex" justifyContent="space-between" mb={3}>
              {[
                {
                  key: 'all', // For recipients, we use 'all'
                  label: 'Recipients',
                  value: campaign ? campaign.recipients : 0,
                  rate: '100%' // Recipients rate is always 100%
                },
                {
                  key: 'opened',
                  label: 'Opens',
                  value: campaign ? campaign.opens : 0,
                  rate: campaign && campaign.recipients
                    ? getPercentage(campaign.opens, campaign.recipients)
                    : '0%'
                },

                {
                  key: 'clicked',
                  label: 'Clicks',
                  value: campaign ? campaign.clicks : 0,
                  rate: campaign && campaign.recipients
                    ? getPercentage(campaign.clicks, campaign.recipients)
                    : '0%'
                },
                {
                  key: 'unsubscribed',
                  label: 'Unsubscribed',
                  value: campaign ? campaign.unsubscribed : 0,
                  rate: campaign && campaign.recipients
                    ? getPercentage(campaign.unsubscribed, campaign.recipients)
                    : '0%'
                }
              ].map((item) => (
                <Box key={item.key} textAlign="center" width="24%">
                  <Typography variant="h5">{item.value}</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {item.label}
                  </Typography>
                  <Typography variant="body2">{item.rate}</Typography>
                  <Button
                    variant="text"
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                    onClick={() => handleFilterClick(item.key)}
                  >
                    View
                  </Button>
                </Box>
              ))}
            </Box>

            {/* Contacts Table */}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  {/* Column Headers in Bold */}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Contact</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date Sent</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date Viewed</TableCell>
                  </TableRow>
                  {/* Filter Row */}
                  <TableRow>
                    <TableCell>
                      <TextField
                        variant="standard"
                        placeholder="Filter"
                        fullWidth
                        value={textFilters.contact}
                        onChange={(e) =>
                          setTextFilters({ ...textFilters, contact: e.target.value })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <FormControl variant="standard" fullWidth>
                        <Select
                          value={textFilters.status}
                          onChange={(e) =>
                            setTextFilters({ ...textFilters, status: e.target.value })
                          }
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>All Statuses</em>
                          </MenuItem>
                          {uniqueStatuses.map((status) => (
                            <MenuItem key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        placeholder="Filter"
                        fullWidth
                        value={textFilters.dateSent}
                        onChange={(e) =>
                          setTextFilters({ ...textFilters, dateSent: e.target.value })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        variant="standard"
                        placeholder="Filter"
                        fullWidth
                        value={textFilters.dateViewed}
                        onChange={(e) =>
                          setTextFilters({ ...textFilters, dateViewed: e.target.value })
                        }
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedContacts.map((contact, index) => {
                    const dateSentStr = contact.dateSent
                      ? new Date(contact.dateSent).toLocaleString()
                      : '-';
                    const dateViewedStr = contact.dateViewed
                      ? new Date(contact.dateViewed).toLocaleString()
                      : '-';
                    return (
                      <TableRow key={index}>
                        <TableCell>{contact.email}</TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            {(() => {
                              const status = contact.status ? contact.status.toLowerCase() : 'n/a';
                              const icon = getStatusIcon(status);
                              const tooltipTitle = contact.errorDetails || (status === 'failed' || status === 'bounced' ? 'Email delivery failed' : '');

                              if (status === 'failed' || status === 'bounced') {
                                return (
                                  <Tooltip title={tooltipTitle}>
                                    <span>{icon}</span>
                                  </Tooltip>
                                );
                              }
                              return icon;
                            })()}
                            {contact.status ? contact.status.charAt(0).toUpperCase() + contact.status.slice(1) : 'N/A'}
                          </Box>
                        </TableCell>
                        <TableCell>{dateSentStr}</TableCell>
                        <TableCell>{dateViewedStr}</TableCell>
                      </TableRow>
                    );
                  })}
                  {paginatedContacts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        No data
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      count={filteredContacts.length}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      onPageChange={handleChangePage}
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      labelRowsPerPage="Rows per page"
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Email Template Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 3, height: '100%' }}>
            {templateLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Loading email template...
                </Typography>
              </Box>
            ) : emailTemplate ? (
              <Box>
                {/* Template Header */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="h6">
                        Email Template Preview
                      </Typography>
                    </Box>
                    <Box display="flex" gap={2} mb={2}>
                      <Chip 
                        label={`Subject: ${emailTemplate.subject}`} 
                        variant="outlined" 
                        size="small" 
                      />
                      <Chip 
                        label={`From: ${emailTemplate.senderEmail}`} 
                        variant="outlined" 
                        size="small" 
                      />
                    </Box>
                  </CardContent>
                </Card>

                {/* Template Content */}
                <Card>
                  <CardContent sx={{ p: 0 }}>
                    <Box
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: '#f9f9f9',
                        minHeight: '400px'
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: '#fff',
                          margin: '20px',
                          borderRadius: '4px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          overflow: 'auto',
                          maxHeight: '500px'
                        }}
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ) : (
              <Box 
                display="flex" 
                flexDirection="column" 
                justifyContent="center" 
                alignItems="center" 
                height="100%"
                sx={{ color: 'text.secondary' }}
              >
                <EmailIcon sx={{ fontSize: 64, mb: 2, color: 'text.disabled' }} />
                <Typography variant="h6" gutterBottom>
                  No Email Template Found
                </Typography>
                <Typography variant="body2" textAlign="center" sx={{ maxWidth: 400 }}>
                  The email template for this campaign could not be found. This might happen if the campaign was created before template storage was implemented.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Box width="100%" display="flex" justifyContent="space-between">
          <BrandButton onClick={exportCampaignData}>
            Export
          </BrandButton>
          <BrandOutlinedButton onClick={onClose}>
            Close
          </BrandOutlinedButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EmailAnalyticsDialog;
